import React from 'react';
import VisaPageLayout, { FaqItem, ProcessStep, RequirementItem, VisaPlan } from './visa-page-layout';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';


const plans: VisaPlan[] = [
  { id: 'sa-standard', name: 'Tourist eVisa', entryType: 'Multiple Entry', validity: 'Valid for 1 year from issue', stay: 'Up to 90 days per visit', processing: 'Same day – 3 business days', price: '480', currency: 'SAR', featured: true },
  { id: 'sa-express', name: 'Tourist eVisa — Express', entryType: 'Multiple Entry', validity: 'Valid for 1 year from issue', stay: 'Up to 90 days per visit', processing: 'Within 24 hours', price: '620', currency: 'SAR' },
  { id: 'sa-umrah', name: 'Umrah Visa Assistance', entryType: 'Single Entry', validity: 'Aligned to your travel dates', stay: 'As specified on your itinerary', processing: '3–5 business days', price: '540', currency: 'SAR' },
];

const requirements: RequirementItem[] = [
  { label: 'Passport copy', detail: 'Valid for at least 6 months from your arrival date.' },
  { label: 'Passport-size photo', detail: 'Recent, plain white or light background, no headwear unless for religious reasons.' },
  { label: 'Confirmed accommodation', detail: 'Hotel booking details for your stay, or itinerary if visiting multiple cities.' },
  { label: 'Return or onward ticket', detail: 'Proof of your planned departure from Saudi Arabia.' },
  { label: 'Health insurance', detail: 'Basic medical coverage for the duration of your trip is required as part of the e-visa.' },
];

const goodToKnow: string[] = [
  'The tourist eVisa allows multiple visits within its one-year validity, capped at 90 days total stay per calendar year for most nationalities.',
  'Rules differ for Hajj and Umrah travel — those follow a separate process handled through accredited agencies, which we can arrange.',
  'Women no longer require a male guardian to apply for or travel on a Saudi tourist visa.',
  'Alcohol remains prohibited nationwide — worth mentioning to travelers unfamiliar with local laws.',
];

const steps: ProcessStep[] = [
  { title: 'Upload your documents', text: 'Share your passport scan, photo and travel dates through our secure form.' },
  { title: 'We review & submit', text: 'Our team checks your file against current Saudi eVisa requirements before filing.' },
  { title: 'Track your status', text: 'We\'ll keep you posted by WhatsApp or email while your application is processed.' },
  { title: 'Receive your eVisa', text: 'Download your approved eVisa and keep a printed copy with your passport at entry.' },
];

const faqs: FaqItem[] = [
  { question: 'Can I combine a Saudi trip with Umrah?', answer: 'Yes, but Umrah travel has its own accredited process. Tell us if you plan to combine tourism and Umrah, and we\'ll set up the right visa route.' },
  { question: 'How many times can I enter Saudi Arabia on the tourist eVisa?', answer: 'It\'s a multiple-entry visa valid for one year, though total time spent in the country is capped per visit and per year — we\'ll walk you through the limits for your nationality.' },
  { question: 'Is there a dress code I should know about?', answer: 'Modest dress is expected in public for all visitors. We include a short cultural guide with every approved visa.' },
  { question: 'What if I need to travel sooner than standard processing allows?', answer: 'Our express option is built for exactly this — ask us and we\'ll confirm same-day feasibility for your nationality.' },
];

export default function VisaKsaPage() {
  const { plans, loading, error } = useVisaPacksByCountry('SAU');
  return (
    <VisaPageLayout
      countryName="Saudi Arabia"
      flagEmoji="🇸🇦"
      heroEyebrow="Kingdom of Saudi Arabia"
      heroImage="https://images.unsplash.com/photo-1710555331394-6f096f01f213?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="Saudi"
      heroTitleTail="Arabia visa, made simple"
      heroSubtitle="From the ancient tombs of AlUla to the towers of Riyadh — we prepare your Saudi tourist eVisa so you can focus on where you're actually going."
      trustStats={[
        { value: '24h', label: 'Express Option' },
        { value: '96%', label: 'Approval Rate' },
        { value: '3,100+', label: 'Saudi Visas Issued' },
      ]}
      plans={plans}
      plansLoading={loading}
      plansError={error}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      faqs={faqs}
      ctaSubtitle="Complete your file today — our express service can have most eVisas approved within 24 hours."
    />
  );
}